import {
	Trigger,
	TriggerLog,
	TriggerStatus,
} from '@/components/triggers/triggerTypes';

import { Json } from '@/database.types';
import { getSupabaseClient, SupabaseConfig } from '@/utils/supabase';
import { v4 as uuidv4 } from 'uuid';

// Define the database row type
interface TriggerDatabaseRow {
	id: string;
	name: string;
	description: string | null;
	product_id: string;
	status: TriggerStatus;
	action: Json;
	trigger_condition: Json;
}

/**
 * Fetch LIVE triggers based on productId
 * @param productId Product ID to filter triggers
 * @param config Optional Supabase configuration
 */
export const fetchLiveTriggers = async (
	productId: string,
	config?: SupabaseConfig
): Promise<{ triggers: Trigger[] | null; error: Error | null }> => {
	const client = getSupabaseClient(config);

	try {
		const { data, error } = await client
			.from('triggers')
			.select('*')
			.eq('product_id', productId)
			.eq('status', 'LIVE');

		if (error) {
			console.error('Error fetching triggers:', error);
			return { triggers: null, error };
		}

		// Convert the DB response to our Trigger type
		const typedTriggers = data?.map((item: TriggerDatabaseRow) => ({
			...item,
			action: item.action as unknown as Trigger['action'],
			trigger_condition:
				item.trigger_condition as unknown as Trigger['trigger_condition'],
		})) as Trigger[];

		return { triggers: typedTriggers, error: null };
	} catch (err) {
		console.error('Error fetching triggers:', err);
		return { triggers: null, error: err as Error };
	}
};

/**
 * Fetch trigger logs for a user based on trigger IDs
 * @param triggerIds Array of trigger IDs to fetch logs for
 * @param userId User ID to filter logs
 * @param config Optional Supabase configuration
 */
export const fetchTriggerLogs = async (
	triggerIds: string[],
	userId: string,
	config?: SupabaseConfig
): Promise<{ logs: TriggerLog[] | null; error: Error | null }> => {
	if (triggerIds.length === 0) {
		return { logs: [], error: null };
	}

	const client = getSupabaseClient(config);

	try {
		const { data, error } = await client
			.from('trigger_logs')
			.select('*')
			.in('trigger_id', triggerIds)
			.eq('product_user_id', userId);

		if (error) {
			console.error('Error fetching trigger logs:', error);
			return { logs: null, error };
		}

		// Convert the DB response to our TriggerLog type
		const typedLogs = data as TriggerLog[];

		return { logs: typedLogs, error: null };
	} catch (err) {
		console.error('Error fetching trigger logs:', err);
		return { logs: null, error: err as Error };
	}
};

/**
 * Subscribe to changes on the trigger_logs table for a specific user
 * @param userId User ID to filter logs
 * @param triggerIds Array of trigger IDs to filter logs
 * @param onUpdate Callback function to handle updates
 * @param config Optional Supabase configuration
 */
export const subscribeTriggerLogs = (
	userId: string,
	triggerIds: string[],
	onUpdate: (logs: TriggerLog[]) => void,
	config?: SupabaseConfig
) => {
	if (triggerIds.length === 0) {
		return { unsubscribe: () => {} };
	}

	const client = getSupabaseClient(config);

	const channel = client
		.channel('trigger-logs-changes')
		.on(
			'postgres_changes',
			{
				event: '*', // Listen for inserts, updates, and deletes
				schema: 'public',
				table: 'trigger_logs',
				filter: `product_user_id=eq.${userId}`,
			},
			() => {
				// Refresh trigger logs when changes occur
				fetchTriggerLogs(triggerIds, userId, config).then(({ logs, error }) => {
					if (!error && logs) {
						onUpdate(logs);
					}
				});
			}
		)
		.subscribe();

	return {
		unsubscribe: () => {
			client.removeChannel(channel);
		},
	};
};

/**
 * Create a new trigger log in Supabase
 * @param triggerId ID of the trigger this log is for
 * @param userId ID of the user who triggered it
 * @param options Optional configuration
 * @param config Optional Supabase configuration
 */
export const createTriggerLog = async (
	triggerId: string,
	userId: string,
	options?: {
		completed?: boolean;
		metadata?: Json;
	},
	config?: SupabaseConfig
): Promise<{ log: TriggerLog | null; error: Error | null }> => {
	const client = getSupabaseClient(config);

	try {
		// First, fetch the trigger to get its product_id
		const { data: triggerData, error: triggerError } = await client
			.from('triggers')
			.select('product_id')
			.eq('id', triggerId)
			.single();

		if (triggerError) {
			console.error('Error fetching trigger:', triggerError);
			return { log: null, error: triggerError };
		}

		const productId = triggerData.product_id;

		// Check if product_user exists with this userId and productId
		const { data: productUserData, error: productUserError } = await client
			.from('product_users')
			.select('id')
			.eq('id', userId)
			.eq('product_id', productId)
			.maybeSingle();

		if (productUserError) {
			console.error('Error checking product_user:', productUserError);
			return { log: null, error: productUserError };
		}

		// If product_user doesn't exist, create it
		if (!productUserData) {
			const { error: createError } = await client.from('product_users').insert({
				id: userId,
				product_id: productId,
				metadata: null,
			});

			if (createError) {
				console.error('Error creating product_user:', createError);
				return { log: null, error: createError };
			}
		}

		// Now create the trigger log
		const logData = {
			id: uuidv4(),
			trigger_id: triggerId,
			product_user_id: userId,
			completed: options?.completed ?? false,
			metadata: options?.metadata ?? null,
		};

		const { data, error } = await client
			.from('trigger_logs')
			.insert(logData)
			.select()
			.single();

		if (error) {
			console.error('Error creating trigger log:', error);
			return { log: null, error };
		}

		return { log: data as TriggerLog, error: null };
	} catch (err) {
		console.error('Error creating trigger log:', err);
		return { log: null, error: err as Error };
	}
};

/**
 * Update an existing trigger log in Supabase
 * @param logId ID of the trigger log to update
 * @param updates Fields to update
 * @param config Optional Supabase configuration
 */
export const updateTriggerLog = async (
	logId: string,
	updates: {
		completed?: boolean;
		metadata?: Json;
	},
	config?: SupabaseConfig
): Promise<{ log: TriggerLog | null; error: Error | null }> => {
	const client = getSupabaseClient(config);

	try {
		const { data, error } = await client
			.from('trigger_logs')
			.update(updates)
			.eq('id', logId)
			.select()
			.single();

		if (error) {
			console.error('Error updating trigger log:', error);
			return { log: null, error };
		}

		return { log: data as TriggerLog, error: null };
	} catch (err) {
		console.error('Error updating trigger log:', err);
		return { log: null, error: err as Error };
	}
};
